import React from 'react';
import styled from 'styled-components';
import {colors} from 'styles/colors';
import {minmax} from 'utils/minmax';

/**
 * 페이지네이션 컴포넌트의 Props 타입
 * @property {number} totalPage - 전체 페이지 수
 * @property {number} currentPage - 현재 페이지
 * @property {React.Dispatch<React.SetStateAction<number>>} setCurrentPage - 현재 페이지 설정 함수
 */
type Props = {
  totalPage: number;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
};

/**
 * 페이지네이션 컴포넌트
 * 페이지 목록을 표시하고 페이지 이동을 처리하는 컴포넌트입니다.
 * @param {Props} props - 페이지네이션 컴포넌트의 props
 * @returns {JSX.Element} 페이지네이션 컴포넌트
 */
const Pagination = ({totalPage, currentPage, setCurrentPage}: Props) => {
  const showPages = 5; // 보여질 최대 페이지 수
  const halfShowPages = Math.floor(showPages / 2);

  // 페이지 목록 시작과 끝 계산
  let startPage = Math.max(currentPage - halfShowPages, 1);
  let endPage = Math.min(startPage + showPages - 1, totalPage);

  // 현재 페이지가 시작 페이지보다 작으면 끝 페이지를 조정
  if (endPage - startPage + 1 < showPages) {
    startPage = Math.max(endPage - showPages + 1, 1);
  }

  // 페이지 목록 생성
  const pages = [];
  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  return (
    <PaginationContainer>
      <Inner>
        <Button
          type="button"
          disabled={currentPage === 1}
          style={{
            color: currentPage === 1 ? colors.lightGrey : colors.black,
            cursor: currentPage === 1 ? 'default' : 'pointer',
          }}
          onClick={() => setCurrentPage(currentPage - 1)}>
          ◀︎
        </Button>

        {/* 페이지 목록 */}
        <NumberWrap>
          {pages.map(page => (
            <Number
              key={page}
              type="button"
              disabled={page === currentPage}
              style={{
                color: page === currentPage ? colors.black : colors.lightGrey,
              }}
              onClick={() => setCurrentPage(page)}>
              {page}
            </Number>
          ))}
        </NumberWrap>

        {/* 다음 페이지 버튼 */}
        <Button
          disabled={totalPage === currentPage}
          type="button"
          style={{
            color: totalPage === currentPage ? colors.lightGrey : colors.black,
            cursor: totalPage === currentPage ? 'default' : 'pointer',
          }}
          onClick={() => setCurrentPage(currentPage + 1)}>
          ▶︎
        </Button>
      </Inner>
    </PaginationContainer>
  );
};

export default Pagination;

const PaginationContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  button {
    height: 100%;
  }
`;

const Inner = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  column-gap: ${minmax(45)};
`;

const NumberWrap = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  column-gap: ${minmax(15)};
`;

const Button = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: ${minmax(23)};
`;

const Number = styled.button`
  font-size: ${minmax(20)};
  &:hover {
    color: ${colors.black} !important;
  }
`;
